/**
 * <description-truncator>
 * ─────────────────────────
 * A custom element that wraps description content and adds
 * "Read more" / "Read less" toggle when the content overflows
 * the CSS line-clamp.
 *
 * Attributes (set via Liquid):
 *   data-read-more-text  — label for the expand button   (default: "Read more")
 *   data-read-less-text  — label for the collapse button  (default: "Read less")
 */

if (!customElements.get('description-truncator')) {
  class DescriptionTruncator extends HTMLElement {
    constructor() {
      super();
      this.content = null;
      this.inner = null;
      this.toggle = null;
      this._resizeObserver = null;
    }

    connectedCallback() {
      this.content = this.querySelector('.desc-truncator__content');
      this.toggle = this.querySelector('.desc-truncator__toggle');

      if (!this.content || !this.toggle) return;

      // The inner element is the text-block / rte-formatter child
      // that actually receives the line-clamp CSS
      this.inner = this.content.firstElementChild;
      if (!this.inner) return;

      // Set button labels from data attributes
      this.readMoreText = this.dataset.readMoreText || 'Read more';
      this.readLessText = this.dataset.readLessText || 'Read less';
      this.toggle.textContent = this.readMoreText;

      // Check overflow after layout settles
      requestAnimationFrame(() => {
        this._checkOverflow();
      });

      // Re-check on resize (responsive breakpoint changes)
      this._resizeObserver = new ResizeObserver(() => {
        // Only re-check when collapsed — if expanded, user is reading
        if (!this.classList.contains('expanded')) {
          this._checkOverflow();
        }
      });
      this._resizeObserver.observe(this.inner);

      // Toggle handler
      this.toggle.addEventListener('click', this._handleToggle.bind(this));
    }

    disconnectedCallback() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    }

    /**
     * Compare scrollHeight vs clientHeight on the INNER element
     * (the one with line-clamp applied) to detect overflow.
     */
    _checkOverflow() {
      if (!this.inner) return;

      // Small threshold (2px) to account for sub-pixel rendering
      const isOverflowing = this.inner.scrollHeight > this.inner.clientHeight + 2;

      if (isOverflowing) {
        this.toggle.classList.add('is-visible');
      } else {
        this.toggle.classList.remove('is-visible');
        // If content fits, make sure we're not stuck in expanded state
        this.classList.remove('expanded');
      }
    }

    _handleToggle() {
      const isExpanded = this.classList.toggle('expanded');

      this.toggle.textContent = isExpanded
        ? this.readLessText
        : this.readMoreText;

      // When collapsing, scroll back to the top of this block if it's out of view
      if (!isExpanded) {
        const rect = this.getBoundingClientRect();
        if (rect.top < 0) {
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        // Re-check overflow after collapse (in case layout changed)
        requestAnimationFrame(() => this._checkOverflow());
      }
    }
  }

  customElements.define('description-truncator', DescriptionTruncator);
}
